// Bestelleingang (docs/bestelleingang/SPEZIFIKATION.md): alle eingehenden Bestellungen an einem Ort – Telefon (KI), E-Mail,
// Bestell-App, Vertrieb, Telefon und Veranstaltungen –, neueste zuerst, mit Zählern je Kanal und „zu prüfen“.
//
//   sales.orderInbox.list    Aufträge des Zeitraums an den Standorten der Person (Recht und Standortbezug wie die Auftragsliste)
//                            und – mit dem Schalter „Anfragen per E-Mail“ und dem Recht, Anliegen zu lesen – die E-Mails, die als
//                            Bestellung eingeordnet, aber noch nicht als Auftrag übernommen sind
//   sales.orderInbox.count   Zähler der Navigation: zu prüfen in den letzten 7 Tagen (Vorgabe der Seite)
//
// Nur lesend, keine eigene Tabelle. Dem KI-Assistenten bleibt die Liste vorenthalten: Sie zeigt Betreff und Absender eingegangener
// E-Mails – fremden Text (docs/anfragen-zentrale/SPEZIFIKATION.md §2). Fragen nach Aufträgen beantwortet sales.order.overview.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { INBOX_CHANNELS, INBOX_HINT_TONES, INBOX_PERIODS, INBOX_VIEWS } from '../lib/orderInbox'
import { zDay } from './common'

const ORDER_READ = 'sales.order.read'

const zCount = z.object({ total: z.int(), review: z.int() })

export const zOrderInboxRow = z.object({
  /** Auftrag (kind 'order') bzw. Anliegen „E-Mail“ (kind 'mail') */
  id: zId,
  kind: z.enum(['order', 'mail']),
  channel: z.enum(INBOX_CHANNELS),
  /** „Telefon (KI)“, „E-Mail“, „Bestell-App“, „Vertrieb“, „Telefon“, „Veranstaltung“ */
  channelLabel: z.string(),
  /** Eingang: Anlage des Auftrags bzw. Eingang der E-Mail */
  receivedAt: z.date(),
  /** Auftragsnummer – erst mit der Bestätigung; bei E-Mails null */
  number: z.string().nullable(),
  /** Kennung des Kanals: Bestellnummer der App („AL-24851“), Telefon-Bestellung („T-100001“), Veranstaltung („VA-2026-00012“) */
  reference: z.string().nullable(),
  /** Kunde; bei Einmalkunden der Name aus dem Auftrag (ohne Kennung); bei E-Mails der erkannte Kunde */
  customer: z.object({ id: zId.nullable(), number: z.string().nullable(), name: z.string() }).nullable(),
  /** E-Mail: Absender (Name bzw. Adresse) */
  sender: z.string().nullable(),
  /** E-Mail: Betreff · Veranstaltung: Anlass · Auftrag aus einer E-Mail: Betreff (nur mit dem Recht, Anliegen zu lesen) */
  detail: z.string().nullable(),
  /** Lieferung oder Abholung; bei E-Mails unbekannt */
  fulfillment: z.enum(['delivery', 'pickup']).nullable(),
  /** Liefer- bzw. Abholtermin; bei E-Mails der erkannte Wunschtermin */
  date: zDay.nullable(),
  /** „08:00–12:00 Uhr“ */
  timeWindow: z.string().nullable(),
  /** brutto in Cent; bei E-Mails null (Preise ermittelt erst der Auftrag) */
  grossCents: z.int().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  /** unbestätigter Auftrag bzw. noch nicht übernommene Bestell-E-Mail */
  needsReview: z.boolean(),
  /** kurze Hinweise zum Prüfen – nur, solange der Eintrag zu prüfen ist */
  hints: z.array(z.object({ code: z.string(), label: z.string(), tone: z.enum(INBOX_HINT_TONES) })),
  /** liefernder Standort (Kürzel); bei E-Mails null */
  locationCode: z.string().nullable(),
  /** Ziel: der Auftrag bzw. die E-Mail */
  href: z.string(),
})

export const orderInboxList = defineContract({
  name: 'sales.orderInbox.list',
  title: 'Bestelleingang anzeigen',
  description:
    'Alle eingehenden Bestellungen an einem Ort – Telefon (KI), E-Mail, Bestell-App, Vertrieb, Telefon und Veranstaltungen –, neueste zuerst, mit Zählern je Kanal und „zu prüfen“.',
  llm: { description: 'Bestelleingang der Oberfläche (mit Betreff und Absender von E-Mails). Für Aufträge: sales.order.overview.', keywords: [] },
  input: z.strictObject({
    /** heute · letzte 7 Tage (Vorgabe) · letzte 30 Tage – heute zählt mit */
    period: z.enum(INBOX_PERIODS).default('7d'),
    /** review = nur zu prüfen · all = alles im Zeitraum (Vorgabe) */
    view: z.enum(INBOX_VIEWS).default('all'),
    channel: z.enum(INBOX_CHANNELS).optional(),
    /** Nummer, Kennung des Kanals, Kunde, Kundennummer; bei E-Mails Betreff und Absender */
    q: z.string().trim().max(100).optional(),
  }),
  output: z.object({
    rows: z.array(zOrderInboxRow),
    /** Zähler im Zeitraum (mit Suche, ohne Kanal- und Ansichtsfilter) */
    counts: z.object({ total: z.int(), review: z.int(), channels: z.record(z.enum(INBOX_CHANNELS), zCount) }),
    /** Beginn des Zeitraums */
    from: z.date(),
    /** zu prüfen, aber vor dem Zeitraum eingegangen (mit Suche und Kanal) */
    reviewBefore: z.int(),
    /** mehr Einträge, als eine Antwort zeigt – die neuesten stehen in `rows` */
    truncated: z.boolean(),
    /** E-Mails sind enthalten (Schalter „Anfragen per E-Mail“ an und Recht, Anliegen zu lesen) */
    mailIncluded: z.boolean(),
  }),
  permission: ORDER_READ,
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})

export const orderInboxCount = defineContract({
  name: 'sales.orderInbox.count',
  title: 'Bestelleingang: zu prüfen zählen',
  description: 'Zählt, was in den letzten 7 Tagen eingegangen und noch zu prüfen ist: unbestätigte Aufträge und noch nicht übernommene Bestell-E-Mails.',
  llm: { description: 'Zähler der Navigation „Bestelleingang“. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({ count: z.int() }),
  permission: ORDER_READ,
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})
